import type { StoreDiagnosticCode } from "@intrasync/stores";

import { isSessionEnded } from "../stores/sessionGuard";
/**
 * The app's OWN unhandled failures, routed into the same sink as the store
 * layer's. Without this the diagnostics panel tells an admin everything about
 * `@intrasync/stores` and nothing about HQ: a TypeError in a render path or a
 * rejected promise in a route hook lands in the console and nowhere else.
 *
 * WHAT EACH SOURCE ACTUALLY CATCHES (checked, not assumed):
 *  - `error` events: uncaught exceptions from any script, plus RESOURCE load
 *    failures (a failed `<script>`/`<img>`), which arrive with no `error`
 *    property and a DOM node as `target`. They are reported separately —
 *    conflating them makes a missing icon look like a crash.
 *  - `unhandledrejection`: a promise that rejects with no catch. This is the
 *    common shape in HQ, where route hooks and store actions are async.
 *  - React ERROR BOUNDARIES: `@intrasync/react`'s ErrorBoundary logs
 *    `console.error("IntraSync Caught Error:", …)` and posts a row through
 *    `logStore.create` — it has no diagnostics hook and no `onError` prop. In a
 *    DEVELOPMENT build React re-throws boundary-caught errors to `window`
 *    first, so the `error` listener below sees them anyway; in a production
 *    build it does not, and the API-side `log` row is the record. Reported as
 *    found rather than papered over.
 *
 * DEDUPED AND BOUNDED. A render loop can throw the same error hundreds of
 * times a second; unthrottled that would fill the 250-slot ring in under a
 * second and evict everything worth keeping. Identical signatures inside the
 * dedupe window are counted, not re-recorded.
 */

interface DiagnosticsHost {
  diagnostics?: {
    error(
      store: string,
      code: StoreDiagnosticCode,
      message: string,
      detail?: { error?: unknown; data?: Record<string, unknown> },
    ): void;
    warn(
      store: string,
      code: StoreDiagnosticCode,
      message: string,
      detail?: { error?: unknown; data?: Record<string, unknown> },
    ): void;
  };
}

/** Repeats of the same signature inside this window are suppressed. */
const DEDUPE_WINDOW_MS = 5_000;

/**
 * Query parameters whose VALUE is a credential. "Sign in with Microsoft"
 * returns the browser to `/auth/login?accessToken=<jwt>`, and the library
 * consumes it asynchronously, so a failure in that window would otherwise copy
 * a live bearer token into the diagnostics buffer and the console. The
 * redactor downstream matches token-shaped KEYS, not a token embedded in a
 * `route` string, so it has to happen here.
 */
const CREDENTIAL_QUERY_PARAMS = new Set([
  "accesstoken",
  "access_token",
  "refreshtoken",
  "refresh_token",
  "id_token",
  "token",
  "temptoken",
  "code",
]);

/** `pathname + search`, with every credential-bearing parameter's value replaced. */
export function routeForDiagnostics(pathname: string, search: string): string {
  if (!search || search === "?") return pathname;
  const params = new URLSearchParams(search);
  let redacted = false;
  for (const key of Array.from(params.keys())) {
    if (CREDENTIAL_QUERY_PARAMS.has(key.toLowerCase())) {
      params.set(key, "[redacted]");
      redacted = true;
    }
  }
  return redacted ? `${pathname}?${params.toString()}` : pathname + search;
}

const currentRoute = (): string =>
  routeForDiagnostics(window.location.pathname, window.location.search);

export function installGlobalErrorHandlers(host: DiagnosticsHost): () => void {
  if (typeof window === "undefined") return () => {};

  const lastSeen = new Map<string, number>();

  const shouldReport = (signature: string): boolean => {
    const now = Date.now();
    const previous = lastSeen.get(signature);
    if (previous !== undefined && now - previous < DEDUPE_WINDOW_MS) {
      return false;
    }
    lastSeen.set(signature, now);
    // The map is keyed by error signature, so it is bounded by the number of
    // DISTINCT failures — but a message carrying a changing id is unbounded.
    if (lastSeen.size > 100) {
      for (const [key, at] of lastSeen) {
        if (now - at >= DEDUPE_WINDOW_MS) lastSeen.delete(key);
      }
    }
    return true;
  };

  const onError = (event: ErrorEvent): void => {
    const target = event.target as (EventTarget & { tagName?: string }) | null;
    // A resource load failure: no `error`, and `target` is the element.
    if (!event.error && target && target !== window && target.tagName) {
      const url =
        (target as unknown as { src?: string; href?: string }).src ??
        (target as unknown as { src?: string; href?: string }).href ??
        "";
      if (!shouldReport(`resource:${target.tagName}:${url}`)) return;
      host.diagnostics?.warn(
        "window",
        "browser.api_failed",
        `Failed to load ${target.tagName.toLowerCase()} resource`,
        { data: { source: "resource", tagName: target.tagName, url } },
      );
      return;
    }

    const message = event.message || "Uncaught error";
    if (!shouldReport(`error:${message}:${event.filename}:${event.lineno}`)) {
      return;
    }
    host.diagnostics?.error("window", "store.unknown", message, {
      error: event.error,
      data: {
        source: "window.onerror",
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
        // Where the user was when it broke — the single most useful field for
        // reproducing a report that arrives as "it froze".
        route: currentRoute(),
      },
    });
  };

  const onRejection = (event: PromiseRejectionEvent): void => {
    // A call the sign-out ended (stores/sessionGuard.ts): expected, not a fault.
    if (isSessionEnded(event.reason)) return;
    const reason = event.reason as { message?: unknown; name?: unknown } | null;
    const message =
      (typeof reason?.message === "string" && reason.message) ||
      (typeof event.reason === "string" && event.reason) ||
      "Unhandled promise rejection";
    if (!shouldReport(`rejection:${message}`)) return;
    host.diagnostics?.error("window", "store.unknown", message, {
      error: event.reason,
      data: {
        source: "unhandledrejection",
        route: currentRoute(),
      },
    });
  };

  // `error` must be captured (resource failures do not bubble).
  window.addEventListener("error", onError, true);
  window.addEventListener("unhandledrejection", onRejection);

  return () => {
    window.removeEventListener("error", onError, true);
    window.removeEventListener("unhandledrejection", onRejection);
    lastSeen.clear();
  };
}

export default installGlobalErrorHandlers;
